import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
    FormControlLabel,
    Radio,
    RadioGroup,
    Link,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { useMemo, useState, useEffect } from 'react';
import { useCommonStyles } from '../../styles/GenericStyle';
import { PATH_NAMES } from '../../routes';
import { useCompleteTrial } from '../../api/vegApl';
import { useGlobalState } from '../../contexts/GlobalState';
import { useSnackbar } from '../../contexts/SnackbarContext';

interface MarkTrialCompletedPopupProps {
    open: boolean;
    onClose: () => void;
    onCompleted?: () => void;
    trialBid: string;
    trialStatus?: string;
    materials: {
        total_materials: number;
        status_breakdown: Record<string, number>;
    };
    isLoading?: boolean;
}

const MarkTrialCompletedPopup = ({
    open,
    onClose,
    onCompleted,
    trialBid,
    trialStatus = '',
    materials,
    isLoading = false,
}: MarkTrialCompletedPopupProps) => {
    const classes = useCommonStyles();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const { state: globalState } = useGlobalState();
    const { showSnackbar } = useSnackbar();
    const [selectedAction, setSelectedAction] = useState('');
    const [isCompletingTrial, setIsCompletingTrial] = useState(false);
    const normalizedTrialStatus = String(trialStatus ?? '').trim().toLowerCase();
    const isReadyForHarvestTrial = normalizedTrialStatus === 'ready for harvest';

    const isMarkAsCompleteEnabled = useMemo(() => {
        const normalizeStatus = (status: string) => status.trim().toLowerCase();
        const allowedStatuses = ['Completed', 'No Seed', 'Ready for Harvest'].map(normalizeStatus);
        const breakdown = materials?.status_breakdown ?? {};
        const disallowedStatuses = Object.keys(breakdown).filter(
            (status) => !allowedStatuses.includes(normalizeStatus(status))
        );
        return disallowedStatuses.length === 0;
    }, [materials]);

    useEffect(() => {
        if (!open) {
            setSelectedAction('');
        }
    }, [open]);

    useEffect(() => {
        if (isMarkAsCompleteEnabled) {
            setSelectedAction('mark-as-complete');
        } else {
            setSelectedAction('move-to-completed');
        }
    }, [isMarkAsCompleteEnabled]);

    const trialDrilldownHref = useMemo(() => {
        const query = new URLSearchParams({
            activeTab: '2',
            trialBid,
        }).toString();
        const base = import.meta.env.BASE_URL.replace(/\/$/, '');
        return `${base}/${PATH_NAMES.SPLotPage}?${query}`;
    }, [trialBid]);

    const summary = useMemo(() => {
        const totalMaterials = materials?.total_materials ?? 0;
        const completedMaterials = materials?.status_breakdown?.Completed ?? 0;
        const percentComplete = totalMaterials > 0
            ? Math.round((completedMaterials / totalMaterials) * 100)
            : 0;

        return {
            totalMaterials,
            completedMaterials,
            incompleteMaterials: Math.max(totalMaterials - completedMaterials, 0),
            percentComplete,
        };
    }, [materials]);

    const statusBreakdown = useMemo(() => {
        const breakdown = materials?.status_breakdown ?? {};
        
        return Object.entries(breakdown)
            .filter(([key]) => key !== 'Completed')
            .map(([label, count]) => ({
                label,
                count: count as number,
            }))
            .filter(item => item.count > 0);
    }, [materials]);

    const { mutate: completeTrialMutation } = useCompleteTrial({
        mutation: {
            onSuccess: (response: any) => {
                setIsCompletingTrial(false);
                const apiMessage = response?.data?.message || response?.data?.detail || 'Trial marked as completed successfully';
                showSnackbar({ message: apiMessage, severity: 'success' });
                onCompleted?.();
                onClose();
            },
            onError: (error: any) => {
                setIsCompletingTrial(false);
                const errorMessage = error?.response?.data?.detail || error?.message || 'Failed to mark trial as completed';
                showSnackbar({ message: errorMessage, severity: 'error' });
            },
        },
    });

    const handleConfirm = () => {
        if (!trialBid) {
            showSnackbar({ message: 'Trial ID is required', severity: 'error' });
            return;
        }

        if (!globalState?.user_id_db) {
            showSnackbar({ message: 'Unable to identify current user', severity: 'error' });
            return;
        }

        setIsCompletingTrial(true);
        completeTrialMutation({
            data: {
                trial_id: trialBid,
                user_id: Number(globalState.user_id_db),
            },
        });
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            fullScreen={fullScreen}
            maxWidth="sm"
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: fullScreen ? 0 : 1.5,
                    height: 'auto',
                },
            }}
        >
            <DialogTitle
                sx={{
                    px: { xs: 2, md: 3 },
                    py: 2,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1.5}>
                    <Stack direction="row" alignItems="center" spacing={1.25}>
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                display: 'grid',
                                placeItems: 'center',
                                bgcolor: '#DCFCE7',
                            }}
                        >
                            <CheckCircleOutlineOutlinedIcon color="success" fontSize="small" />
                        </Box>
                        <Box>
                            <Typography variant="h6" className={classes.labelNoto20Bold700}>
                                Mark Trial as Completed
                            </Typography>
                            <Typography variant="body2" color="text.secondary" className={classes.labelNoto14Bold400}>
                                {trialBid ? `Review material status for ${trialBid}` : 'Review material status'}
                            </Typography>
                        </Box>
                    </Stack>
                    <IconButton onClick={onClose} size="small" aria-label="close mark trial completed popup">
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent
                sx={{
                    px: { xs: 2, md: 3 },
                    pt: 2,
                    pb: 3,
                    mt:'24px',
                    bgcolor: 'background.default',
                    maxHeight: '70vh',
                    overflowY: 'auto',
                }}
            >
                {isLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 180 }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : (
                    <Stack spacing={2}>
                        <Box sx={{
                            borderRadius: 1.5,
                            p: 2,
                            bgcolor: '#EFF6FF',
                            border: `1px solid ${theme.palette.divider}`,
                        }}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                                <Typography className={classes.labelNoto14Bold700}>Material Summary</Typography>
                                <Stack direction="row" alignItems="center" spacing={0.75}>
                                    <Typography className={classes.labelNoto20Bold700} sx={{ color: '#2563EB' }}>
                                        {summary.percentComplete}%
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">Complete</Typography>
                                </Stack>
                            </Stack>
                            <Stack direction="row" spacing={1.5}>
                                <Box sx={{
                                    display: 'flex',
                                    padding: '13px 13px 1px 13px',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    gap: '4px',
                                    alignSelf: 'stretch',
                                    justifySelf: 'stretch',
                                    borderRadius: '10px',
                                    border: '1px solid #BEDBFF',
                                    background: '#FFF',
                                    flex: 1,
                                }}>
                                    <Typography variant="caption" color="text.secondary" className={classes.labelNoto12Bold400}>
                                        Total Materials
                                    </Typography>
                                    <Typography className={classes.labelNoto20Bold700}>{summary.totalMaterials}</Typography>
                                </Box>
                                <Box sx={{
                                    display: 'flex',
                                    padding: '13px 13px 1px 13px',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    gap: '4px',
                                    alignSelf: 'stretch',
                                    justifySelf: 'stretch',
                                    borderRadius: '10px',
                                    border: '1px solid #BEDBFF',
                                    background: '#FFF',
                                    flex: 1,
                                }}>
                                    <Typography variant="caption" color="text.secondary" className={classes.labelNoto12Bold400}>
                                        Completed
                                    </Typography>
                                    <Typography className={classes.labelNoto20Bold700} sx={{ color: '#16A34A' }}>
                                        {summary.completedMaterials}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Box>

                        {(summary.incompleteMaterials > 0 || statusBreakdown.length > 0) && (
                            <Box sx={{
                                display: 'flex',
                                padding: '17px 17px 5px 17px',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                gap: '16px',
                                flexShrink: 0,
                                alignSelf: 'stretch',
                                borderRadius: '10px',
                                border: '1px solid #FEE685',
                                background: '#FFFBEB',
                            }}>
                                <Stack direction="row" alignItems="flex-start" spacing={1}>
                                    <WarningAmberOutlinedIcon sx={{ color: '#D97706', fontSize: 20, mt: '2px', flexShrink: 0 }} />
                                    <Box>
                                        <Typography className={classes.labelNoto14Bold700}>
                                            {summary.incompleteMaterials} Materials Not Completed
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" className={classes.labelNoto12Bold400}>
                                            Review the status breakdown below before marking this trial as completed
                                        </Typography>
                                    </Box>
                                </Stack>

                                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {statusBreakdown.map((item, idx) => {
                                        const dotColors: Record<string, string> = {
                                            'Processing': '#2563EB',
                                            'Registered': '#7C3AED',
                                            'Unknown': '#64748B',
                                        };
                                        const dotColor = dotColors[item.label] ?? '#F59E0B';
                                        return (
                                            <Stack
                                                key={idx}
                                                direction="row"
                                                alignItems="center"
                                                justifyContent="space-between"
                                                sx={{
                                                    display: 'flex',
                                                    height: '38px',
                                                    px: '12px',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    flexShrink: 0,
                                                    alignSelf: 'stretch',
                                                    borderRadius: '4px',
                                                    border: '1px solid #FEF3C6',
                                                    background: '#FFF',
                                                }}
                                            >
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <Box sx={{
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: '50%',
                                                        bgcolor: dotColor,
                                                        flexShrink: 0,
                                                    }} />
                                                    <Typography className={classes.labelNoto14Bold600}>
                                                        {item.label}
                                                    </Typography>
                                                </Stack>
                                                <Typography className={classes.labelNoto14Bold600}>
                                                    {item.count} materials
                                                </Typography>
                                            </Stack>
                                        );
                                    })}
                                </Box>
                            </Box>
                        )}

                        <Box sx={{
                            display: 'flex',
                            height: '82px',
                            padding: '17px 0 17px 17px',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'flex-start',
                            alignSelf: 'stretch',
                            borderRadius: '10px',
                            border: '1px solid #E5E7EB',
                            background: '#F9FAFB',
                        }}>
                            <Typography variant="body2" className={classes.labelNoto14Bold400} sx={{ mb: 1 }}>
                                Need to review materials in detail before completing?
                            </Typography>
                            <Link
                                href={trialDrilldownHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                underline="hover"
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.75,
                                    color: '#0F57DE',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                }}
                            >
                                <OpenInNewOutlinedIcon sx={{ fontSize: 16 }} />
                                Open Trial Drilldown
                            </Link>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Typography className={classes.labelNoto14Bold700}>
                                {isMarkAsCompleteEnabled ? 'Mark Trial as Completed' : 'What should happen to incomplete materials?'}
                                <Box component="span" sx={{ color: theme.palette.error.main }}>
                                    {' '}*
                                </Box>
                            </Typography>
                            <Box sx={{
                                flexShrink: 0,
                                alignSelf: 'stretch',
                                borderRadius: '10px',
                                border: '2px solid #E5E7EB',
                                background: '#FFF',
                            }}>
                                <RadioGroup
                                    value={selectedAction}
                                    onChange={(e) => setSelectedAction(e.target.value)}
                                    sx={{ p: '14px 12px' }}
                                >
                                    {isMarkAsCompleteEnabled ? (
                                        <FormControlLabel
                                            value="mark-as-complete"
                                            disabled={isReadyForHarvestTrial}
                                            control={<Radio sx={{ p: 0, mt: '2px' }} />}
                                            label={
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                    <Typography className={classes.labelNoto14Bold600}>
                                                        Mark as Complete
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" className={classes.labelNoto12Bold400}>
                                                        {isReadyForHarvestTrial
                                                            ? "Cannot mark as completed as trial is in ready for harvest status"
                                                            : "All materials in ready for harvest status will be moved to 'No Seed' or 'Completed' status based on registered gram. A remark of 'Status updated manually' will be added to such materials. Use this option only if no seeds are possible from these materials."}
                                                    </Typography>
                                                </Box>
                                            }
                                            sx={{
                                                m: 0,
                                                alignItems: 'flex-start',
                                                gap: '12px',
                                                '& .MuiFormControlLabel-label': {
                                                    m: 0,
                                                },
                                            }}
                                        />
                                    ) : (
                                        <FormControlLabel
                                            value="move-to-completed"
                                            disabled={true}
                                            control={<Radio sx={{ p: 0, mt: '2px' }} />}
                                            label={
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                    <Typography className={classes.labelNoto14Bold600}>
                                                        Move to Completed
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" className={classes.labelNoto12Bold400}>
                                                        Cannot mark as complete as materials are there in status not applicable to be marked as complete.
                                                    </Typography>
                                                </Box>
                                            }
                                            sx={{
                                                m: 0,
                                                alignItems: 'flex-start',
                                                gap: '12px',
                                                '& .MuiFormControlLabel-label': {
                                                    m: 0,
                                                },
                                            }}
                                        />
                                    )}
                                </RadioGroup>
                            </Box>
                        </Box>
                    </Stack>
                )}
            </DialogContent>

            <DialogActions
                sx={{
                    px: { xs: 2, md: 3 },
                    py: 2,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    justifyContent: 'flex-end',
                }}
            >
                <Button
                    variant="contained"
                    className={classes.greenButton}
                    disabled={isLoading || isCompletingTrial || !selectedAction || !isMarkAsCompleteEnabled || isReadyForHarvestTrial}
                    onClick={handleConfirm}
                    sx={{
                        minWidth: 120,
                    }}
                >
                    {isCompletingTrial ? 'Confirming...' : 'Confirm'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MarkTrialCompletedPopup;
